import {
  HyamCookiePreferencesLink,
  isCookiePreferencesItem,
} from "hyam-core/components/HyamIubenda";
import _ from "lodash";
import { FooterDocument } from "prismicio-types";
import { styles as moduleStyles, HyamPrismicDocumentFooterStyles } from "./styles";
import {
  HyamPrismicRichText,
  HyamPrismicButton,
  HyamPrismicLink,
  HyamPrismicImage,
  HyamPrismicDocumentFooterMobile,
} from "hyam-prismic/components";
import { withConfigure } from "hyam-core/utils/withConfigure";
import { useBreakpointIndex } from "hyam-core/hooks/useBreakpoint";
import ArrowWhite from "svg/arrow-right-24px_white.svg";

export type HyamPrismicDocumentFooterProps = {
  document: FooterDocument;
  styles?: Record<string, string>;
  socialMediaLinksPosition?: "top" | "bottom";
};

type FooterSectionProps = HyamPrismicDocumentFooterProps & {
  sectionSlices: "slices1" | "slices2" | "slices3";
  className: string;
};

const FooterSection = (props: FooterSectionProps) => {
  const slices = props.document.data?.[props.sectionSlices];
  const styles = props.styles as HyamPrismicDocumentFooterStyles;

  return (
    <div className={props.className}>
      {_.map(slices, (slice, key) => {
        if (slice.slice_type === "footer_column_rich_text") {
          return (
            <div className={styles.column_rich_text} key={key}>
              <HyamPrismicRichText
                field={slice.primary.content}
                styles={{
                  all: styles.rich_text_all,
                  paragraph: styles.rich_text_paragraph,
                }}
                stylesReplace={{
                  hyperlink: styles.rich_text_hyperlink,
                }}
              />
              <HyamPrismicButton
                field={slice.primary.button_link}
                styles={{ container: styles.column_rich_text_button }}
                rightIcon={<ArrowWhite />}
              >
                {slice.primary.button_label}
              </HyamPrismicButton>
            </div>
          );
        }
        if (slice.slice_type === "footer_column_links_with_title") {
          return (
            <div className={styles.column_links_with_title} key={key}>
              {/* NOTE: add &nbsp; for empty titles to have a height */}
              <strong className={styles.column_title}>{slice.primary.title}&nbsp;</strong>
              {_.map(slice.items, (item, key) => {
                // "Cookie Policy" opens the Iubenda preferences popup
                if (isCookiePreferencesItem(item)) {
                  return (
                    <HyamCookiePreferencesLink key={key} className={styles.column_link}>
                      {item.link_label}
                    </HyamCookiePreferencesLink>
                  );
                }

                return (
                  <HyamPrismicLink
                    key={key}
                    field={item.link}
                    className={styles.column_link}
                  >
                    {item.link_label}
                  </HyamPrismicLink>
                );
              })}
            </div>
          );
        }
        if (slice.slice_type === "footer_column_action_items") {
          return (
            <div className={styles.column_action_items} key={key}>
              <strong className={styles.column_action_item_title}>
                {slice.primary.item_1_title}
              </strong>
              <HyamPrismicButton
                field={slice.primary.item_1_link}
                styles={{ container: styles.column_action_item_link }}
                rightIcon={<ArrowWhite />}
              >
                {slice.primary.item_1_link_label_desktop}
              </HyamPrismicButton>
              <strong className={styles.column_action_item_title}>
                {slice.primary.item_2_title}
              </strong>
              <HyamPrismicButton
                field={slice.primary.item_2_link}
                styles={{ container: styles.column_action_item_link }}
                rightIcon={<ArrowWhite />}
              >
                {slice.primary.item_2_link_label_desktop}
              </HyamPrismicButton>
              <strong className={styles.column_action_item_title}>
                {slice.primary.item_3_title}
              </strong>
              <div className={styles.column_action_item_social}>
                {_.map(slice.items, (item, key) => {
                  return (
                    <HyamPrismicLink
                      key={key}
                      field={item.social_media_link}
                      className={styles.column_action_item_social_link}
                    >
                      <img
                        width={24}
                        height={24}
                        src={item.social_media_icon.url || ""}
                        alt={item.social_media_icon.alt || ""}
                      />
                      ;
                    </HyamPrismicLink>
                  );
                })}
              </div>
            </div>
          );
        }
      })}
    </div>
  );
};

const _HyamPrismicDocumentFooter = (props: HyamPrismicDocumentFooterProps) => {
  const styles = props.styles as HyamPrismicDocumentFooterStyles;

  const document = props.document;
  const breakpoint = useBreakpointIndex();
  if (breakpoint < 0) return <div className={styles.container_ssg} />;

  if (breakpoint < 4) return <HyamPrismicDocumentFooterMobile document={document} />;

  if (!document?.data) return <div>HyamPrismicDocumentFooter: Missing data</div>;

  return (
    <footer className={styles.container}>
      <div className={styles.layout_container}>
        <FooterSection
          {...props}
          className={styles.top_section}
          sectionSlices="slices1"
        />
        <FooterSection
          {...props}
          className={styles.middle_section}
          sectionSlices="slices2"
        />
        <FooterSection
          {...props}
          className={styles.bottom_section}
          sectionSlices="slices3"
        />
        <div className={styles.image_container}>
          <HyamPrismicImage
            sizesByBreakpoint={{ sm: 1, lg: 1, xl: 1 }}
            field={document.data.logo}
          />
        </div>
      </div>
    </footer>
  );
};

export const HyamPrismicDocumentFooter = withConfigure<
  HyamPrismicDocumentFooterProps,
  HyamPrismicDocumentFooterStyles
>({
  componentName: "HyamPrismicDocumentFooter",
  Component: _HyamPrismicDocumentFooter,
  moduleStyles,
});
